/**
 * Read/write scope builder for a role binding rule.
 */
import React from 'react';
import { X, Info, Lock } from 'lucide-react';
import { AVAILABLE_SCOPE_RELATIONS, SCOPE_LABELS } from '@/components/roles/role-configs';
import {
  applyReadScopeLogicToggle,
  applyReadScopeRelationToggle,
  deriveReadScopeViewState,
  getScopePreview,
} from '@/components/roles/role-form-utils';
import type { RuleReadScope } from '@/types/role.types';

/** Props for {@link ReadScopeBuilder}. */
interface ReadScopeBuilderProps {
  value: RuleReadScope | null | undefined;
  onChange: (scope: RuleReadScope | null) => void;
  disabled?: boolean;
  label?: string | false;
  /** Constrains available options based on a parent visibility scope */
  constrainedBy?: RuleReadScope | null;
  /** Text used in the natural-language preview. Defaults to "Limited to" (read and write). */
  previewPrefix?: string;
}

/** CSS classes for a relation chip. */
function relationChipClass(isLocked: boolean, isSelected: boolean, disabled?: boolean): string {
  const base =
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors';
  if (isLocked)
    return `${base} bg-amber-100 text-amber-800 ring-2 ring-amber-300 cursor-not-allowed`;
  if (isSelected) return `${base} bg-blue-100 text-blue-800 hover:bg-blue-200`;
  return `${base} bg-gray-100 text-gray-700 hover:bg-gray-200 ${
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
  }`;
}

/** Read/write scope builder for a role binding rule. */
export function ReadScopeBuilder({
  value,
  onChange,
  disabled,
  label = 'Quelles données peut-il voir ?',
  constrainedBy,
  previewPrefix = 'Limité à',
}: Readonly<ReadScopeBuilderProps>) {
  const availableRelations = AVAILABLE_SCOPE_RELATIONS;
  const { selectedRelations, logic, lockedRelations, isConstrained, isUnrestricted } =
    deriveReadScopeViewState(value, constrainedBy);

  const toggleUnrestricted = () => {
    if (disabled || isConstrained) return; // Cannot select unrestricted when constrained
    onChange(null);
  };

  const toggleRelation = (relationCode: string) => {
    if (disabled) return;

    // Cannot toggle locked relations
    if (lockedRelations.includes(relationCode)) return;

    onChange(applyReadScopeRelationToggle(value, relationCode, constrainedBy));
  };

  const toggleLogic = () => {
    if (disabled || selectedRelations.length < 2 || isConstrained) return; // Cannot change logic when constrained
    onChange(applyReadScopeLogicToggle(value));
  };

  return (
    <div className="space-y-3">
      {label !== false && <div className="text-sm font-medium text-gray-700">{label}</div>}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={toggleUnrestricted}
          disabled={disabled || isConstrained}
          className={`
            inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
            transition-colors
            ${
              isUnrestricted
                ? 'bg-green-100 text-green-800 ring-2 ring-green-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }
            ${disabled || isConstrained ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
          title={
            isConstrained
              ? 'Limité par le périmètre de lecture'
              : SCOPE_LABELS.unrestricted?.description
          }
        >
          {SCOPE_LABELS.unrestricted?.label || 'Toutes les données'}
          {isUnrestricted && <span className="text-green-600">✓</span>}
        </button>

        {availableRelations.map((relationCode) => {
          const label = SCOPE_LABELS[relationCode];
          const isSelected = selectedRelations.includes(relationCode);
          const isLocked = lockedRelations.includes(relationCode);

          return (
            <button
              key={relationCode}
              type="button"
              onClick={() => toggleRelation(relationCode)}
              disabled={disabled || isLocked}
              className={relationChipClass(isLocked, isSelected, disabled)}
              title={
                isLocked
                  ? `${label?.description} (Obligatoire - défini par le périmètre de lecture)`
                  : label?.description
              }
            >
              {isLocked && <Lock className="w-3 h-3" />}
              {label?.label || relationCode}
              {isSelected && !isLocked && <X className="w-3 h-3" />}
            </button>
          );
        })}

        {selectedRelations.length >= 2 && (
          <div className="inline-flex items-center gap-2 px-2 py-1 bg-gray-50 rounded-full border border-gray-200">
            <span className="text-xs text-gray-600">Si plusieurs :</span>
            <button
              type="button"
              onClick={toggleLogic}
              disabled={disabled || isConstrained}
              className={`
                px-2 py-0.5 rounded text-xs font-medium transition-colors
                ${
                  logic === 'OR'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }
                ${disabled || isConstrained ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              Au moins un
            </button>
            <button
              type="button"
              onClick={toggleLogic}
              disabled={disabled || isConstrained}
              className={`
                px-2 py-0.5 rounded text-xs font-medium transition-colors
                ${
                  logic === 'AND'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }
                ${disabled || isConstrained ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              Tous
            </button>
          </div>
        )}
      </div>

      {isConstrained && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded text-sm text-amber-900 border border-amber-200">
          <Lock className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
          <span>
            Les actions ne peuvent porter que sur les données visibles. Les critères de lecture sont
            obligatoires pour les actions.
          </span>
        </div>
      )}

      {value !== undefined && (selectedRelations.length > 0 || value !== null) && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 rounded text-sm text-blue-900">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
          <span>
            {isUnrestricted
              ? "Toutes les données de l'organisation"
              : `${previewPrefix} : ${getScopePreview(selectedRelations, logic)}`}
          </span>
        </div>
      )}
    </div>
  );
}
